// Système de logging pour le frontend
class Logger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000; // Limite le nombre de logs en mémoire
    this.isEnabled = true;
    this.logLevel = 'info'; // debug, info, warn, error
    
    // Niveaux de log avec priorités
    this.levels = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3
    };
    
    // Initialiser le logger
    this.init();
  }

  init() {
    // Ajouter le logger à la console du navigateur
    if (typeof window !== 'undefined') {
      window.ATSLogger = this;
      
      // Intercepter les erreurs globales
      window.addEventListener('error', (event) => {
        this.error('Erreur globale', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error
        });
      });

      // Intercepter les promesses rejetées
      window.addEventListener('unhandledrejection', (event) => {
        this.error('Promesse rejetée non gérée', {
          reason: event.reason,
          promise: event.promise
        });
      });

      // Log au démarrage
      this.info('Logger ATS initialisé', {
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      });
    }
  }

  shouldLog(level) {
    return this.isEnabled && this.levels[level] >= this.levels[this.logLevel];
  }

  formatMessage(level, message, data = null) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      message,
      data,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
    };

    // Ajouter au tableau des logs
    this.logs.push(logEntry);
    
    // Limiter le nombre de logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    return logEntry;
  }

  debug(message, data = null) {
    if (!this.shouldLog('debug')) return;
    
    const logEntry = this.formatMessage('debug', message, data);
    console.log(`🔍 [DEBUG] ${message}`, data || '');
    
    return logEntry;
  }

  info(message, data = null) {
    if (!this.shouldLog('info')) return;
    
    const logEntry = this.formatMessage('info', message, data);
    console.info(`ℹ️ [INFO] ${message}`, data || '');
    
    return logEntry;
  }

  warn(message, data = null) {
    if (!this.shouldLog('warn')) return;
    
    const logEntry = this.formatMessage('warn', message, data);
    console.warn(`⚠️ [WARN] ${message}`, data || '');
    
    return logEntry;
  }

  error(message, data = null) {
    if (!this.shouldLog('error')) return;
    
    const logEntry = this.formatMessage('error', message, data);
    console.error(`❌ [ERROR] ${message}`, data || '');
    
    return logEntry;
  }

  // Log spécifique pour les APIs
  api(method, url, requestData = null, responseData = null, status = null, duration = null) {
    const message = `API ${method} ${url}`;
    const data = {
      method,
      url,
      requestData,
      responseData,
      status,
      duration: duration ? `${duration}ms` : null,
      timestamp: new Date().toISOString()
    };

    if (status >= 400) {
      this.error(message, data);
    } else if (status >= 300) {
      this.warn(message, data);
    } else {
      this.info(message, data);
    }

    return data;
  }

  // Log spécifique pour les composants React
  component(componentName, action, data = null) {
    const message = `[${componentName}] ${action}`;
    this.debug(message, data);
  }

  // Log spécifique pour les routes
  route(from, to) {
    this.info('Navigation', {
      from,
      to,
      timestamp: new Date().toISOString()
    });
  }

  // Log spécifique pour les performances
  performance(action, duration, data = null) {
    const message = `Performance: ${action}`;
    const logData = {
      action,
      duration: `${duration}ms`,
      ...data
    };

    if (duration > 1000) {
      this.warn(message, logData);
    } else {
      this.debug(message, logData);
    }
  }

  // Obtenir tous les logs
  getLogs(level = null, limit = null) {
    let filteredLogs = this.logs;
    
    if (level) {
      filteredLogs = filteredLogs.filter(log => log.level === level);
    }
    
    if (limit) {
      filteredLogs = filteredLogs.slice(-limit);
    }
    
    return filteredLogs;
  }

  // Obtenir les logs d'erreur
  getErrors() {
    return this.logs.filter(log => log.level === 'error');
  }

  // Obtenir les logs d'API
  getApiLogs() {
    return this.logs.filter(log => log.data && log.data.method);
  }

  // Exporter les logs
  exportLogs() {
    return {
      logs: this.logs,
      summary: {
        total: this.logs.length,
        errors: this.getErrors().length,
        warnings: this.logs.filter(log => log.level === 'warn').length,
        apiCalls: this.getApiLogs().length
      },
      exportTime: new Date().toISOString()
    };
  }

  // Vider les logs
  clearLogs() {
    this.logs = [];
    this.info('Logs vidés');
  }

  // Activer/désactiver le logger
  setEnabled(enabled) {
    this.isEnabled = enabled;
    this.info(`Logger ${enabled ? 'activé' : 'désactivé'}`);
  }

  // Changer le niveau de log
  setLogLevel(level) {
    if (this.levels[level] !== undefined) {
      this.logLevel = level;
      this.info(`Niveau de log changé vers: ${level}`);
    } else {
      this.warn(`Niveau de log invalide: ${level}`);
    }
  }
}

// Créer une instance globale
const logger = new Logger();

export default logger;
